import { useState } from 'react';
import { CompressOutlined, ExpandAltOutlined, ReloadOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { ChevronRight, ListTree, Sparkles } from 'lucide-react';
import { SectionExtraButtons } from './ExtraButtons';
import NodeOps from './NodeOps';
import {
    chapterNumbers,
    countOutlineResources,
    hasOwnNumbering,
    isLeafContentChapter,
} from './outlineUtils';
import PremiumExtraTags from './PremiumExtraTags';
import styles from './PremiumOutlineTree.module.scss';

const stop = (event) => event.stopPropagation();

function Caret({ expanded, label, onToggle }) {
    return (
        <button
            type="button"
            className={styles.caret}
            aria-expanded={expanded}
            aria-label={label}
            onClick={(event) => {
                event.stopPropagation();
                onToggle();
            }}
        >
            <ChevronRight
                size={15}
                aria-hidden
                className={classNames(styles.caretIcon, { [styles.caretOpen]: expanded })}
            />
        </button>
    );
}

function ChildResourceCount({ nodes }) {
    const { figures, tables } = countOutlineResources(nodes);
    if (!figures && !tables) return null;
    return (
        <span className={styles.childCount} title="子级最末级小节中已选择的图表，不重复计入父级">
            子级含 {figures} 图 · {tables} 表
        </span>
    );
}

function PlansRow({ node, detail }) {
    if (!node.extras?.length) return null;
    return (
        <div className={styles.plansRow}>
            <PremiumExtraTags
                extras={node.extras}
                extraDesc={node.extraDesc}
                sectionData={node}
                detail={detail}
            />
        </div>
    );
}

function SubsectionItem({ chapter, section, sub, number, ctx }) {
    const showNumber = number && !hasOwnNumbering(sub.chapter);
    return (
        <li className={styles.subsection}>
            <div className={styles.row}>
                <span className={styles.caretSpacer} aria-hidden />
                {showNumber && <span className={classNames(styles.no, styles.subNo)}>{number}</span>}
                <div className={styles.main}>
                    <div className={styles.subTitle}>{sub.chapter}</div>
                    {sub.chapterAbstract && <p className={styles.abstract}>{sub.chapterAbstract}</p>}
                </div>
                <PlansRow node={sub} detail={ctx.detail} />
                <div className={styles.actions} onClick={stop}>
                    <NodeOps
                        className={styles.ops}
                        onEdit={() => ctx.handleEditChapter(sub, true, section.id, 3, chapter.id)}
                        siblingLabel="添加同级子节"
                        onAddSibling={() =>
                            ctx.handleAddChapter(sub.id, true, section.id, 3, chapter.id)
                        }
                        onDelete={() =>
                            ctx.handleDeleteChapter(sub.id, true, section.id, 3, chapter.id)
                        }
                    />
                    <div className={styles.extraSlot}>
                        <SectionExtraButtons
                            sectionId={sub.id}
                            parentId={section.id}
                            existingExtras={sub.extras}
                            grandParentId={chapter.id}
                            sectionData={sub}
                            {...ctx.extraButtonProps}
                        />
                    </div>
                </div>
            </div>
        </li>
    );
}

function SectionItem({ chapter, section, number, ctx }) {
    const subs = section.subsections || [];
    const hasSubs = subs.length > 0;
    const expanded = ctx.expandedSections.has(section.id);
    const showNumber = number && !hasOwnNumbering(section.chapter);

    return (
        <li className={styles.section}>
            <div
                className={classNames(styles.row, { [styles.rowClickable]: hasSubs })}
                onClick={() => hasSubs && ctx.toggleSection(section.id)}
            >
                {hasSubs ? (
                    <Caret
                        expanded={expanded}
                        label={`${expanded ? '折叠' : '展开'} ${section.chapter} 的小节`}
                        onToggle={() => ctx.toggleSection(section.id)}
                    />
                ) : (
                    <span className={styles.caretSpacer} aria-hidden />
                )}
                {showNumber && <span className={styles.no}>{number}</span>}
                <div className={styles.main}>
                    <div className={styles.titleLine}>
                        <span className={styles.sectionTitle}>{section.chapter}</span>
                        {hasSubs && <span className={styles.countChip}>{subs.length} 小节</span>}
                    </div>
                    {section.chapterAbstract && (
                        <p className={styles.abstract}>{section.chapterAbstract}</p>
                    )}
                    <ChildResourceCount nodes={subs} />
                </div>
                <PlansRow node={section} detail={ctx.detail} />
                <div className={styles.actions} onClick={stop}>
                    <NodeOps
                        className={styles.ops}
                        onEdit={() => ctx.handleEditChapter(section, true, chapter.id)}
                        siblingLabel="添加同级节"
                        childLabel="添加子节"
                        onAddSibling={() => ctx.handleAddChapter(section.id, true, chapter.id)}
                        onAddChild={() =>
                            ctx.handleAddChapter(
                                hasSubs ? subs[subs.length - 1].id : null,
                                true,
                                section.id,
                                3,
                                chapter.id,
                            )
                        }
                        onDelete={() => ctx.handleDeleteChapter(section.id, true, chapter.id)}
                    />
                    <div className={styles.extraSlot}>
                        <SectionExtraButtons
                            sectionId={section.id}
                            parentId={chapter.id}
                            existingExtras={section.extras}
                            grandParentId={null}
                            sectionData={section}
                            {...ctx.extraButtonProps}
                        />
                    </div>
                </div>
            </div>

            {expanded && hasSubs && (
                <ol className={styles.subsections}>
                    {subs.map((sub, subIdx) => (
                        <SubsectionItem
                            key={sub.id}
                            chapter={chapter}
                            section={section}
                            sub={sub}
                            number={number ? `${number}.${subIdx + 1}` : ''}
                            ctx={ctx}
                        />
                    ))}
                </ol>
            )}
        </li>
    );
}

function ChapterBlock({ chapter, chIdx, number, ctx }) {
    const sections = chapter.sections || [];
    const hasSections = sections.length > 0;
    const expanded = ctx.expandedChapters.has(chapter.id);
    const leaf = isLeafContentChapter(chapter);
    const streaming = ctx.isStreaming && chIdx === ctx.streamingChapterIdx;
    // 标题自带「第一章」时不再显示章号；摘要 / 参考文献等固定章不占号
    const showNumber = number && !hasOwnNumbering(chapter.chapter);

    return (
        <section
            data-chapter-idx={chIdx}
            className={classNames(styles.chapter, { [styles.streaming]: streaming })}
        >
            <div
                className={classNames(styles.row, styles.chapterRow, {
                    [styles.rowClickable]: hasSections,
                })}
                onClick={() => hasSections && ctx.toggleChapter(chapter.id)}
            >
                {hasSections ? (
                    <Caret
                        expanded={expanded}
                        label={`${expanded ? '折叠' : '展开'}${chapter.chapter}`}
                        onToggle={() => ctx.toggleChapter(chapter.id)}
                    />
                ) : (
                    <span className={styles.caretSpacer} aria-hidden />
                )}
                {showNumber && <span className={styles.chapterNo}>第{number}章</span>}
                <div className={styles.main}>
                    <div className={styles.titleLine}>
                        <span className={styles.chapterTitle}>{chapter.chapter}</span>
                        {hasSections && (
                            <span className={styles.countChip}>{sections.length} 节</span>
                        )}
                    </div>
                    {chapter.chapterAbstract && (
                        <p className={styles.abstract}>{chapter.chapterAbstract}</p>
                    )}
                    <ChildResourceCount nodes={sections} />
                </div>
                {leaf && <PlansRow node={chapter} detail={ctx.detail} />}
                {chapter.chapter !== '参考文献' && (
                    <div className={styles.actions} onClick={stop}>
                        <NodeOps
                            className={styles.ops}
                            onEdit={() => ctx.handleEditChapter(chapter)}
                            siblingLabel="添加同级章节"
                            childLabel="添加子节"
                            onAddSibling={() => ctx.handleAddChapter(chapter.id)}
                            onAddChild={() =>
                                ctx.handleAddChapter(
                                    hasSections ? sections[sections.length - 1].id : null,
                                    true,
                                    chapter.id,
                                    2,
                                )
                            }
                            onDelete={() => ctx.handleDeleteChapter(chapter.id)}
                        />
                        {/* 没有节的叶子章（开题报告 / 文献综述的模块）也能配图表：parentId 为空表示作用在章上 */}
                        {leaf && (
                            <div className={styles.extraSlot}>
                                <SectionExtraButtons
                                    sectionId={chapter.id}
                                    parentId={null}
                                    existingExtras={chapter.extras}
                                    grandParentId={null}
                                    sectionData={chapter}
                                    {...ctx.extraButtonProps}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>

            {expanded && hasSections && (
                <ol className={styles.sections}>
                    {sections.map((section, secIdx) => (
                        <SectionItem
                            key={section.id}
                            chapter={chapter}
                            section={section}
                            number={number ? `${number}.${secIdx + 1}` : ''}
                            ctx={ctx}
                        />
                    ))}
                </ol>
            )}
        </section>
    );
}

/**
 * 学术特调引擎的大纲页：特调会为每张图给出很长的结构化说明，
 * 这里用文档式层级 + 字段卡片展示；普通引擎使用 OutlineTree。props 与 OutlineTree 一致。
 */
export default function PremiumOutlineTree({
    outlineData,
    isStreaming,
    streamingChapterIdx,
    expandedChapters,
    expandedSections,
    allExpanded,
    outlineContainerRef,
    generateOutline,
    regenerateDisabled,
    toggleChapter,
    toggleSection,
    toggleAllExpand,
    handleEditChapter,
    handleAddChapter,
    handleDeleteChapter,
    extraButtonProps,
}) {
    const [detail, setDetail] = useState('compact');
    const chapters = Array.isArray(outlineData) ? outlineData : [];
    const numbers = chapterNumbers(chapters);
    const sectionCount = chapters.reduce((sum, chapter) => sum + (chapter.sections?.length || 0), 0);
    const resourceCount = countOutlineResources(chapters);
    const ctx = {
        detail,
        expandedChapters,
        expandedSections,
        isStreaming,
        streamingChapterIdx,
        toggleChapter,
        toggleSection,
        handleEditChapter,
        handleAddChapter,
        handleDeleteChapter,
        extraButtonProps,
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.toolbar}>
                <div className={styles.toolbarLeft}>
                    <span className={styles.toolbarIcon} aria-hidden>
                        <ListTree size={18} />
                    </span>
                    <span className={styles.toolbarTitle}>文章大纲</span>
                    <span className={styles.premiumBadge}>
                        <Sparkles size={12} aria-hidden />
                        学术特调
                    </span>
                    <span className={styles.stat}>
                        共 {chapters.length} 章 · {sectionCount} 节
                    </span>
                    <span
                        className={classNames(styles.stat, styles.statStrong)}
                        title="按最末级小节的实际选择统计；同一小节选择多个图类型时分别计数"
                    >
                        {isStreaming ? '当前已规划' : '已规划'} {resourceCount.figures} 图 ·{' '}
                        {resourceCount.tables} 表
                    </span>
                </div>
                <div className={styles.toolbarRight}>
                    <div className={styles.segmented} role="group" aria-label="图表说明显示方式">
                        <button
                            type="button"
                            className={classNames(styles.segment, {
                                [styles.segmentActive]: detail === 'compact',
                            })}
                            aria-pressed={detail === 'compact'}
                            onClick={() => setDetail('compact')}
                        >
                            精简
                        </button>
                        <button
                            type="button"
                            className={classNames(styles.segment, {
                                [styles.segmentActive]: detail === 'full',
                            })}
                            aria-pressed={detail === 'full'}
                            onClick={() => setDetail('full')}
                        >
                            详细
                        </button>
                    </div>
                    <button
                        type="button"
                        className={styles.toolbarBtn}
                        disabled={regenerateDisabled}
                        onClick={generateOutline}
                    >
                        <ReloadOutlined /> 重新生成
                    </button>
                    <button type="button" className={styles.toolbarBtn} onClick={toggleAllExpand}>
                        {allExpanded ? <CompressOutlined /> : <ExpandAltOutlined />}
                        {allExpanded ? '全部折叠' : '全部展开'}
                    </button>
                </div>
            </div>

            <div className={styles.list} ref={outlineContainerRef}>
                {chapters.map((chapter, chIdx) => (
                    <ChapterBlock
                        key={chapter.id}
                        chapter={chapter}
                        chIdx={chIdx}
                        number={numbers[chIdx]}
                        ctx={ctx}
                    />
                ))}
            </div>
        </div>
    );
}
